import React, { useMemo } from 'react'
import {
  Box, Typography, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material'
import { format, parseISO, getISODay } from 'date-fns'

const EngagementTimelineGrid = ({ craneDays = [], planningColors = {}, planningLabels = {} }) => {
  // Group crane days: project -> plant -> date -> data
  const { projects, dates, weekHeaders } = useMemo(() => {
    const projectMap = {}
    const dateSet = new Set()

    craneDays.forEach((cd) => {
      if (!projectMap[cd.projectId]) {
        projectMap[cd.projectId] = {
          id: cd.projectId,
          name: cd.projectName,
          plants: {},
        }
      }
      if (!projectMap[cd.projectId].plants[cd.plantId]) {
        projectMap[cd.projectId].plants[cd.plantId] = {
          id: cd.plantId,
          name: cd.plantName,
          days: {},
        }
      }
      projectMap[cd.projectId].plants[cd.plantId].days[cd.date] = cd
      dateSet.add(cd.date)
    })

    const sortedDates = [...dateSet].sort()

    // Build week headers
    const weeks = []
    let currentWeekStart = null
    let currentWeekDates = 0
    sortedDates.forEach((d) => {
      const dow = getISODay(parseISO(d))
      if (dow === 1 || currentWeekStart === null) {
        if (currentWeekStart !== null) {
          weeks.push({ label: format(parseISO(currentWeekStart), 'dd/MM'), span: currentWeekDates })
        }
        currentWeekStart = d
        currentWeekDates = 1
      } else {
        currentWeekDates++
      }
    })
    if (currentWeekStart !== null) {
      weeks.push({ label: format(parseISO(currentWeekStart), 'dd/MM'), span: currentWeekDates })
    }

    return {
      projects: Object.values(projectMap),
      dates: sortedDates,
      weekHeaders: weeks,
    }
  }, [craneDays])

  if (!craneDays.length) {
    return <Typography variant="body2" color="text.secondary">No data available for this period.</Typography>
  }

  const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto', maxHeight: '70vh', position: 'relative' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          {/* Week header row */}
          <TableRow>
            <TableCell sx={{ minWidth: 220, maxWidth: 220, position: 'sticky', left: 0, zIndex: 4, backgroundColor: 'white' }} />
            {weekHeaders.map((w, i) => (
              <TableCell key={i} colSpan={w.span} align="center" sx={{ fontSize: 11, fontWeight: 'bold', borderLeft: '1px solid #ddd' }}>
                {w.label}
              </TableCell>
            ))}
          </TableRow>
          {/* Day-of-week header row */}
          <TableRow>
            <TableCell sx={{ minWidth: 220, maxWidth: 220, position: 'sticky', left: 0, zIndex: 4, backgroundColor: 'white' }} />
            {dates.map((d) => (
              <TableCell key={d} align="center" sx={{ fontSize: 10, p: 0.5, minWidth: 28 }}>
                {dayLabels[getISODay(parseISO(d)) - 1]}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map((project) => (
            <React.Fragment key={project.id}>
              {/* Project header row */}
              <TableRow>
                <TableCell
                  colSpan={dates.length + 1}
                  sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', position: 'sticky', left: 0, zIndex: 3 }}
                >
                  {project.name}
                </TableCell>
              </TableRow>
              {/* Crane rows */}
              {Object.values(project.plants).map((plant) => (
                <React.Fragment key={plant.id}>
                  {/* Plan row */}
                  <TableRow>
                    <TableCell sx={{
                      fontSize: 11, position: 'sticky', left: 0, zIndex: 2,
                      backgroundColor: 'white', borderRight: '1px solid #ddd',
                      minWidth: 220, maxWidth: 220, whiteSpace: 'nowrap'
                    }}>
                      <strong>{plant.name}</strong> Plan
                    </TableCell>
                    {dates.map((d) => {
                      const cd = plant.days[d]
                      if (!cd) return <TableCell key={d} sx={{ p: 0.5 }} />
                      const color = planningColors[cd.planningClassification] || '#eee'
                      const label = planningLabels[cd.planningClassification] || cd.planningClassification
                      return (
                        <Tooltip key={d} title={`${d}: ${label} (score: ${cd.planningScore})`} arrow>
                          <TableCell
                            sx={{
                              p: 0.5,
                              backgroundColor: color,
                              textAlign: 'center',
                              fontSize: 10,
                              color: 'white',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              minWidth: 28,
                            }}
                          >
                            {cd.planningClassification === 'no_plan' ? '' : cd.planningScore}
                          </TableCell>
                        </Tooltip>
                      )
                    })}
                  </TableRow>
                  {/* Operator engagement row */}
                  <TableRow>
                    <TableCell sx={{
                      fontSize: 11, position: 'sticky', left: 0, zIndex: 2,
                      backgroundColor: 'white', borderRight: '1px solid #ddd', borderBottom: '2px solid #ccc',
                      minWidth: 220, maxWidth: 220, whiteSpace: 'nowrap'
                    }}>
                      <strong>{plant.name}</strong> Operator
                    </TableCell>
                    {dates.map((d) => {
                      const cd = plant.days[d]
                      if (!cd) return <TableCell key={d} sx={{ p: 0.5, borderBottom: '2px solid #ccc' }} />
                      const hrs = cd.engagementHours
                      const intensity = Math.min(hrs / 6, 1)
                      const bg = hrs > 0
                        ? `rgba(33, 150, 243, ${0.15 + intensity * 0.7})`
                        : '#fff'
                      return (
                        <Tooltip key={d} title={`${d}: ${hrs.toFixed(1)}h engagement`} arrow>
                          <TableCell
                            sx={{
                              p: 0.5,
                              backgroundColor: bg,
                              textAlign: 'center',
                              fontSize: 10,
                              cursor: 'pointer',
                              minWidth: 28,
                              borderBottom: '2px solid #ccc',
                            }}
                          >
                            {hrs > 0 ? hrs.toFixed(1) : ''}
                          </TableCell>
                        </Tooltip>
                      )
                    })}
                  </TableRow>
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default EngagementTimelineGrid
